import { useState, useEffect, useCallback, useRef } from "react";
import {
  ArrowLeft,
  Brain,
  Send,
  Loader2,
  Play,
  Check,
  ChevronRight,
  Sparkles,
  Bot,
  AlertTriangle,
  X,
  RotateCcw,
  CheckCircle2,
  Circle,
  MessageSquare,
  Zap,
  Upload,
  FileText,
  ThumbsUp,
  ThumbsDown,
  SkipForward,
  Shield,
  Database,
  BookMarked,
  GitBranch,
  FlaskConical,
  BrainCircuit,
  Eye,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  apiv2,
  type OrchestratorPlanOut,
  type OrchestratorStepOut,
  type OrchestratorStepStatus,
  type OrchestratorDocumentOut,
} from "../apiv2";
import { getAgent } from "../agents";
import GreenTendrils from "./GreenTendrils";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface Props {
  projectId: string;
  projectName?: string;
  onLaunchAgent: (agentId: string) => void;
  onBack: () => void;
}

// ---------------------------------------------------------------------------
// Status configuration
// ---------------------------------------------------------------------------
const STATUS_CONFIG: Record<
  OrchestratorStepStatus,
  { icon: typeof Circle; color: string; label: string; bg: string }
> = {
  pending: {
    icon: Circle,
    color: "text-white/20",
    label: "Pending",
    bg: "rgba(255,255,255,0.02)",
  },
  waiting_input: {
    icon: MessageSquare,
    color: "text-amber-400",
    label: "Needs your input",
    bg: "rgba(251,191,36,0.04)",
  },
  running: {
    icon: Loader2,
    color: "text-brand-400",
    label: "Running",
    bg: "rgba(134,188,37,0.04)",
  },
  waiting_approval: {
    icon: Eye,
    color: "text-blue-400",
    label: "Review & Approve",
    bg: "rgba(59,130,246,0.04)",
  },
  approved: {
    icon: CheckCircle2,
    color: "text-green-400",
    label: "Approved",
    bg: "rgba(34,197,94,0.04)",
  },
  rejected: {
    icon: ThumbsDown,
    color: "text-red-400",
    label: "Rejected",
    bg: "rgba(239,68,68,0.04)",
  },
  completed: {
    icon: CheckCircle2,
    color: "text-green-400",
    label: "Completed & Approved",
    bg: "rgba(34,197,94,0.04)",
  },
  skipped: {
    icon: SkipForward,
    color: "text-white/30",
    label: "Skipped",
    bg: "rgba(255,255,255,0.02)",
  },
  failed: {
    icon: AlertTriangle,
    color: "text-red-400",
    label: "Failed",
    bg: "rgba(239,68,68,0.04)",
  },
};

// Statuses that count as "done" for progress + the resolved (green check)
// step treatment. `approved` is a conditional step whose HITL gate the
// reviewer approved — terminal, same as a completed non-conditional step.
const RESOLVED_STATUSES = new Set<OrchestratorStepStatus>([
  "completed",
  "skipped",
  "approved",
]);

// Agent icon mapping
const AGENT_ICONS: Record<string, typeof Brain> = {
  "backlog-architect": BrainCircuit,
  "data-guardian": Shield,
  "semantic-intelligence": Database,
  "metadata-curator": BookMarked,
  "data-pipeline-creator": GitBranch,
  "test-planner": FlaskConical,
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function AutonomousOrchestrator({
  projectId,
  projectName,
  onLaunchAgent,
  onBack,
}: Props) {
  const [plan, setPlan] = useState<OrchestratorPlanOut | null>(null);
  const [goalInput, setGoalInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [answerInputs, setAnswerInputs] = useState<Record<string, string>>({});
  const [submittingAnswer, setSubmittingAnswer] = useState<string | null>(null);
  const [runningStep, setRunningStep] = useState<string | null>(null);
  const [approvingStep, setApprovingStep] = useState<string | null>(null);
  const [rejectFeedback, setRejectFeedback] = useState<Record<string, string>>(
    {},
  );
  const [showRejectInput, setShowRejectInput] = useState<string | null>(null);
  const [documents, setDocuments] = useState<OrchestratorDocumentOut[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Load existing plan + documents ──
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [existingPlan, docs] = await Promise.all([
          apiv2.getOrchestratorPlan(projectId),
          apiv2.listOrchestratorDocuments(projectId),
        ]);
        if (!mounted) return;
        setPlan(existingPlan);
        setDocuments(docs);
      } catch (err: any) {
        if (mounted) setError(err.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [projectId]);

  // ── Document upload ──
  const handleDocUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploading(true);
      setError("");
      try {
        const doc = await apiv2.uploadOrchestratorDocument(projectId, file);
        setDocuments((prev) => [doc, ...prev]);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [projectId],
  );

  // ── Create plan ──
  const handleCreatePlan = useCallback(async () => {
    if (!goalInput.trim()) return;
    setCreating(true);
    setError("");
    try {
      const docIds = documents.map((d) => d.id);
      const newPlan = await apiv2.createOrchestratorPlan(
        projectId,
        goalInput.trim(),
        docIds,
      );
      setPlan(newPlan);
      setGoalInput("");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  }, [projectId, goalInput, documents]);

  // ── Answer question ──
  const handleAnswerQuestion = useCallback(
    async (stepId: string) => {
      const answer = answerInputs[stepId]?.trim();
      if (!answer) return;
      setSubmittingAnswer(stepId);
      setError("");
      try {
        const updated = await apiv2.answerOrchestratorQuestion(
          projectId,
          stepId,
          answer,
        );
        setPlan(updated);
        setAnswerInputs((prev) => ({ ...prev, [stepId]: "" }));
      } catch (err: any) {
        setError(err.message);
      } finally {
        setSubmittingAnswer(null);
      }
    },
    [projectId, answerInputs],
  );

  // ── Run step ──
  const handleRunStep = useCallback(
    async (stepId: string) => {
      setRunningStep(stepId);
      setError("");
      try {
        const updated = await apiv2.runOrchestratorStep(projectId, stepId);
        setPlan(updated);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setRunningStep(null);
      }
    },
    [projectId],
  );

  // ── Complete step (move to waiting_approval) ──
  const handleCompleteStep = useCallback(
    async (stepId: string) => {
      setError("");
      try {
        const updated = await apiv2.completeOrchestratorStep(projectId, stepId);
        setPlan(updated);
      } catch (err: any) {
        setError(err.message);
      }
    },
    [projectId],
  );

  // ── Approve step ──
  const handleApproveStep = useCallback(
    async (stepId: string) => {
      setApprovingStep(stepId);
      setError("");
      try {
        const updated = await apiv2.approveOrchestratorStep(
          projectId,
          stepId,
          true,
        );
        setPlan(updated);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setApprovingStep(null);
      }
    },
    [projectId],
  );

  // ── Reject step ──
  const handleRejectStep = useCallback(
    async (stepId: string) => {
      const feedback = rejectFeedback[stepId]?.trim() || "Please revise";
      setApprovingStep(stepId);
      setError("");
      try {
        const updated = await apiv2.rejectOrchestratorStep(
          projectId,
          stepId,
          feedback,
        );
        setPlan(updated);
        setShowRejectInput(null);
        setRejectFeedback((prev) => ({ ...prev, [stepId]: "" }));
      } catch (err: any) {
        setError(err.message);
      } finally {
        setApprovingStep(null);
      }
    },
    [projectId, rejectFeedback],
  );

  // ── Skip step ──
  const handleSkipStep = useCallback(
    async (stepId: string) => {
      setError("");
      try {
        const updated = await apiv2.skipOrchestratorStep(projectId, stepId);
        setPlan(updated);
      } catch (err: any) {
        setError(err.message);
      }
    },
    [projectId],
  );

  // ── Cancel plan ──
  const handleCancel = useCallback(async () => {
    if (!plan) return;
    try {
      await apiv2.cancelOrchestratorPlan(projectId);
      setPlan(null);
    } catch (err: any) {
      setError(err.message);
    }
  }, [projectId, plan]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-brand-400" size={32} />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-dark-950 overflow-hidden">
      <GreenTendrils />
      <div className="relative z-10 max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/5 transition"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <Brain size={20} className="text-brand-400" />
              Autonomous Pipeline
            </h1>
            {projectName && (
              <p className="text-xs text-white/30 mt-0.5">{projectName}</p>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div
            className="p-3 rounded-xl text-sm text-red-300 border border-red-500/20 flex items-center gap-2"
            style={{ background: "rgba(239,68,68,0.06)" }}
          >
            <AlertTriangle size={16} /> {error}
            <button
              onClick={() => setError("")}
              className="ml-auto text-red-400 hover:text-red-300"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* ── No Plan: Setup view ── */}
        {!plan && (
          <div className="space-y-6 animate-fade-in">
            {/* Document upload section */}
            <div
              className="glass rounded-2xl p-6"
              style={{
                background: "rgba(28,28,42,0.6)",
                border: "1px solid rgba(134,188,37,0.1)",
              }}
            >
              <div className="flex items-center gap-2 mb-4">
                <FileText size={16} className="text-brand-400" />
                <span className="text-xs font-bold text-brand-400 uppercase tracking-wider">
                  Requirements Documents
                </span>
              </div>
              <p className="text-xs text-white/40 mb-4">
                Upload BRD, PRD, or any requirements documents. These will be
                analyzed by the pipeline agents.
              </p>

              {/* Uploaded documents */}
              {documents.length > 0 && (
                <div className="space-y-2 mb-4">
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg border border-white/5"
                      style={{ background: "rgba(255,255,255,0.02)" }}
                    >
                      <FileText size={14} className="text-brand-400" />
                      <span className="text-sm text-white/60 flex-1 truncate">
                        {doc.filename}
                      </span>
                      <span className="text-[11px] text-white/20 uppercase">
                        {doc.file_type}
                      </span>
                      <span className="text-[11px] text-white/20">
                        {(doc.file_size / 1024).toFixed(1)} KB
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium text-brand-400 border border-brand-500/30 hover:border-brand-500/50 transition disabled:opacity-30"
                style={{ background: "rgba(134,188,37,0.05)" }}
              >
                {uploading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Upload size={14} />
                )}
                {uploading ? "Uploading..." : "Upload Document"}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx,.xlsx,.xls,.pdf,.txt,.md,.csv,.json"
                onChange={handleDocUpload}
                className="hidden"
              />
            </div>

            {/* Goal input */}
            <div
              className="glass rounded-2xl p-6"
              style={{
                background: "rgba(28,28,42,0.6)",
                border: "1px solid rgba(134,188,37,0.1)",
              }}
            >
              <div className="flex items-center gap-2 mb-4">
                <Sparkles size={16} className="text-brand-400" />
                <span className="text-xs font-bold text-brand-400 uppercase tracking-wider">
                  Describe Your Goal
                </span>
              </div>
              <textarea
                value={goalInput}
                onChange={(e) => setGoalInput(e.target.value)}
                placeholder="Describe what you want to achieve... e.g., 'Build a complete data warehouse for our customer analytics platform with masked PII, full metadata catalog, and automated testing'"
                rows={4}
                className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-white/20 resize-none focus:outline-none border"
                style={{
                  background: "rgba(14,14,24,0.6)",
                  borderColor: "rgba(134,188,37,0.15)",
                }}
              />

              <div className="flex items-center justify-between mt-4">
                <p className="text-[11px] text-white/20">
                  The pipeline will be configured based on your goal
                  {documents.length > 0
                    ? ` and ${documents.length} uploaded document${documents.length > 1 ? "s" : ""}`
                    : ""}
                  .
                </p>
                <button
                  onClick={handleCreatePlan}
                  disabled={creating || !goalInput.trim()}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-30 transition-all"
                  style={{
                    background: "linear-gradient(135deg, #86BC25, #6B9B1E)",
                  }}
                >
                  {creating ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Zap size={16} />
                  )}
                  Generate Pipeline
                </button>
              </div>
            </div>

            {/* Pipeline preview */}
            <div
              className="glass rounded-2xl p-6"
              style={{
                background: "rgba(28,28,42,0.4)",
                border: "1px solid rgba(255,255,255,0.04)",
              }}
            >
              <p className="text-xs font-bold text-white/30 uppercase tracking-wider mb-4">
                Pipeline Stages
              </p>
              <div className="space-y-1">
                {[
                  {
                    icon: BrainCircuit,
                    name: "Agent Scrum Planner",
                    desc: "Epics & User Stories from requirements",
                    tag: "Required",
                  },
                  {
                    icon: Shield,
                    name: "Agent Data Guardian",
                    desc: "PII detection & masking",
                    tag: "Conditional",
                  },
                  {
                    icon: Database,
                    name: "Agent Semantic Intelligence",
                    desc: "Semantic model, DDL, ER diagrams",
                    tag: "Required",
                  },
                  {
                    icon: BookMarked,
                    name: "Agent Metadata Curator",
                    desc: "Column metadata & business glossary",
                    tag: "Conditional",
                  },
                  {
                    icon: GitBranch,
                    name: "Agent Pipeline Engineer",
                    desc: "ETL/ELT pipeline generation",
                    tag: "Conditional",
                  },
                  {
                    icon: FlaskConical,
                    name: "Agent QA Strategist",
                    desc: "Test strategy & test scripts",
                    tag: "Conditional",
                  },
                ].map((stage, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg"
                  >
                    <div className="flex items-center justify-center w-6 h-6 rounded-full border border-white/10 text-white/20 text-[11px] font-mono">
                      {idx + 1}
                    </div>
                    <stage.icon size={16} className="text-brand-400/60" />
                    <div className="flex-1 min-w-0">
                      <span className="text-xs text-white/50">
                        {stage.name}
                      </span>
                      <span className="text-[11px] text-white/20 ml-2">
                        — {stage.desc}
                      </span>
                    </div>
                    <span
                      className={`text-[11px] px-2 py-0.5 rounded-full ${stage.tag === "Required" ? "text-brand-400 border border-brand-500/20" : "text-white/20 border border-white/5"}`}
                    >
                      {stage.tag}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Active Plan: Pipeline view ── */}
        {plan && (
          <div className="space-y-6 animate-fade-in">
            {/* Plan header */}
            <div
              className="glass rounded-2xl p-6"
              style={{
                background: "rgba(28,28,42,0.6)",
                border: "1px solid rgba(134,188,37,0.1)",
              }}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Brain size={16} className="text-brand-400" />
                    <span className="text-xs font-bold text-brand-400 uppercase tracking-wider">
                      {plan.status === "completed"
                        ? "Pipeline Complete"
                        : "Active Pipeline"}
                    </span>
                  </div>
                  <p className="text-sm text-white/60 leading-relaxed">
                    "{plan.goal}"
                  </p>
                </div>
                {(plan.status === "in_progress" ||
                  plan.status === "active") && (
                  <button
                    onClick={handleCancel}
                    className="text-xs text-white/20 hover:text-red-400 px-3 py-1.5 rounded-lg border border-white/5 hover:border-red-500/30 transition-all"
                  >
                    Cancel
                  </button>
                )}
              </div>

              {/* Progress bar */}
              <div className="mt-4">
                <div className="flex items-center justify-between text-[11px] text-white/30 mb-1">
                  <span>Progress</span>
                  <span>
                    {
                      plan.steps.filter((s) => RESOLVED_STATUSES.has(s.status))
                        .length
                    }{" "}
                    / {plan.steps.length} steps
                  </span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${(plan.steps.filter((s) => RESOLVED_STATUSES.has(s.status)).length / Math.max(plan.steps.length, 1)) * 100}%`,
                      background: "linear-gradient(90deg, #86BC25, #6B9B1E)",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Pipeline steps */}
            <div className="space-y-3">
              {plan.steps.map((step, idx) => (
                <PipelineStepCard
                  key={step.id}
                  step={step}
                  stepNumber={idx + 1}
                  isActive={
                    idx === plan.current_step_index &&
                    (plan.status === "in_progress" || plan.status === "active")
                  }
                  isLast={idx === plan.steps.length - 1}
                  answerInput={answerInputs[step.id] || ""}
                  onAnswerChange={(val) =>
                    setAnswerInputs((prev) => ({ ...prev, [step.id]: val }))
                  }
                  onSubmitAnswer={() => handleAnswerQuestion(step.id)}
                  submittingAnswer={submittingAnswer === step.id}
                  onRun={() => handleRunStep(step.id)}
                  onComplete={() => handleCompleteStep(step.id)}
                  onApprove={() => handleApproveStep(step.id)}
                  onReject={() => handleRejectStep(step.id)}
                  onSkip={() => handleSkipStep(step.id)}
                  runningStep={runningStep === step.id}
                  approvingStep={approvingStep === step.id}
                  rejectFeedback={rejectFeedback[step.id] || ""}
                  onRejectFeedbackChange={(val) =>
                    setRejectFeedback((prev) => ({ ...prev, [step.id]: val }))
                  }
                  showRejectInput={showRejectInput === step.id}
                  onToggleRejectInput={() =>
                    setShowRejectInput(
                      showRejectInput === step.id ? null : step.id,
                    )
                  }
                  onLaunchAgent={() => onLaunchAgent(step.agent_id)}
                />
              ))}
            </div>

            {/* Completed — new plan button */}
            {plan.status === "completed" && (
              <div className="flex justify-center">
                <button
                  onClick={() => setPlan(null)}
                  className="flex items-center gap-2 px-6 py-3 text-sm font-medium text-brand-400 rounded-xl border border-brand-500/30 hover:border-brand-500/50 transition-all"
                  style={{ background: "rgba(134,188,37,0.05)" }}
                >
                  <RotateCcw size={16} /> Start a new pipeline
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pipeline Step Card
// ---------------------------------------------------------------------------
function PipelineStepCard({
  step,
  stepNumber,
  isActive,
  isLast,
  answerInput,
  onAnswerChange,
  onSubmitAnswer,
  submittingAnswer,
  onRun,
  onComplete,
  onApprove,
  onReject,
  onSkip,
  runningStep,
  approvingStep,
  rejectFeedback,
  onRejectFeedbackChange,
  showRejectInput,
  onToggleRejectInput,
  onLaunchAgent,
}: {
  step: OrchestratorStepOut;
  stepNumber: number;
  isActive: boolean;
  isLast: boolean;
  answerInput: string;
  onAnswerChange: (val: string) => void;
  onSubmitAnswer: () => void;
  submittingAnswer: boolean;
  onRun: () => void;
  onComplete: () => void;
  onApprove: () => void;
  onReject: () => void;
  onSkip: () => void;
  runningStep: boolean;
  approvingStep: boolean;
  rejectFeedback: string;
  onRejectFeedbackChange: (val: string) => void;
  showRejectInput: boolean;
  onToggleRejectInput: () => void;
  onLaunchAgent: () => void;
}) {
  const [expanded, setExpanded] = useState(isActive);
  const { icon: StatusIcon, color, label, bg } = STATUS_CONFIG[step.status];
  const AgentIcon = AGENT_ICONS[step.agent_id] || Bot;
  const agentDef = getAgent(step.agent_id);

  // Auto-expand active steps
  useEffect(() => {
    if (isActive) setExpanded(true);
  }, [isActive]);

  const isResolved = RESOLVED_STATUSES.has(step.status);

  return (
    <div className="relative">
      {/* Connector line */}
      {!isLast && (
        <div
          className="absolute left-[27px] top-[56px] bottom-[-12px] w-px"
          style={{
            background: isResolved
              ? "rgba(134,188,37,0.3)"
              : "rgba(255,255,255,0.06)",
          }}
        />
      )}

      <div
        className={`glass rounded-2xl overflow-hidden transition-all duration-300 ${
          isActive ? "ring-1 ring-brand-500/30" : ""
        }`}
        style={{
          background: isActive ? bg : "rgba(28,28,42,0.4)",
          border: `1px solid ${isActive ? "rgba(134,188,37,0.2)" : "rgba(255,255,255,0.04)"}`,
        }}
      >
        {/* Step header — always visible */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full p-4 flex items-center gap-3 text-left hover:bg-white/[0.02] transition"
        >
          {/* Step number circle */}
          <div
            className={`flex items-center justify-center w-[38px] h-[38px] rounded-full border-2 shrink-0 ${
              isResolved
                ? "border-green-500/40 bg-green-500/10"
                : isActive
                  ? "border-brand-500/40 bg-brand-500/10"
                  : "border-white/10 bg-white/[0.02]"
            }`}
          >
            {isResolved ? (
              step.status === "skipped" ? (
                <SkipForward size={16} className="text-white/30" />
              ) : (
                <Check size={16} className="text-green-400" />
              )
            ) : (
              <span
                className={`text-xs font-bold ${isActive ? "text-brand-400" : "text-white/20"}`}
              >
                {stepNumber}
              </span>
            )}
          </div>

          {/* Agent info */}
          <AgentIcon
            size={18}
            className={
              isResolved
                ? "text-green-400/60"
                : isActive
                  ? "text-brand-400"
                  : "text-white/20"
            }
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3
                className={`text-sm font-bold ${isResolved ? "text-white/40" : "text-white"}`}
              >
                {step.title}
              </h3>
              {step.is_conditional && (
                <span className="text-[11px] px-1.5 py-0.5 rounded border border-white/5 text-white/20">
                  Optional
                </span>
              )}
            </div>
            <p className="text-[11px] text-white/30 truncate">
              {agentDef?.shortName || step.agent_id}
            </p>
          </div>

          {/* Status badge */}
          <div className={`flex items-center gap-1.5 ${color}`}>
            <StatusIcon
              size={14}
              className={step.status === "running" ? "animate-spin" : ""}
            />
            <span className="text-[11px] font-medium hidden sm:inline">
              {label}
            </span>
          </div>

          {/* Expand chevron */}
          {expanded ? (
            <ChevronUp size={14} className="text-white/20" />
          ) : (
            <ChevronDown size={14} className="text-white/20" />
          )}
        </button>

        {/* Expanded content */}
        {expanded && (
          <div className="px-4 pb-4 pt-0 space-y-4 border-t border-white/[0.04]">
            <p className="text-xs text-white/40 mt-3 leading-relaxed">
              {step.description}
            </p>

            {/* Question + answer input */}
            {step.status === "waiting_input" && step.question && (
              <div className="space-y-3">
                <div
                  className="p-3 rounded-xl text-xs text-amber-300/80 leading-relaxed"
                  style={{
                    background: "rgba(251,191,36,0.06)",
                    border: "1px solid rgba(251,191,36,0.15)",
                  }}
                >
                  <MessageSquare
                    size={12}
                    className="inline mr-1.5 text-amber-400"
                  />
                  {step.question}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={answerInput}
                    onChange={(e) => onAnswerChange(e.target.value)}
                    placeholder={
                      step.is_conditional
                        ? "Type 'Yes' to proceed or 'No' to skip..."
                        : "Type your answer..."
                    }
                    className="flex-1 px-3 py-2 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none border"
                    style={{
                      background: "rgba(14,14,24,0.6)",
                      borderColor: "rgba(255,255,255,0.08)",
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") onSubmitAnswer();
                    }}
                  />
                  <button
                    onClick={onSubmitAnswer}
                    disabled={submittingAnswer || !answerInput.trim()}
                    className="px-4 py-2 rounded-lg text-xs font-medium text-white disabled:opacity-30 flex items-center gap-1.5"
                    style={{
                      background: "linear-gradient(135deg, #86BC25, #6B9B1E)",
                    }}
                  >
                    {submittingAnswer ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Send size={14} />
                    )}
                  </button>
                  {step.is_conditional && (
                    <button
                      onClick={onSkip}
                      className="px-3 py-2 rounded-lg text-xs text-white/30 hover:text-white/50 border border-white/5 hover:border-white/10 transition"
                    >
                      <SkipForward size={14} />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* User's previous response */}
            {step.user_response && step.status !== "waiting_input" && (
              <div
                className="p-2 rounded-lg text-xs text-white/40"
                style={{ background: "rgba(255,255,255,0.02)" }}
              >
                <span className="text-white/20">Your answer:</span>{" "}
                {step.user_response}
              </div>
            )}

            {/* Run button (pending state) */}
            {step.status === "pending" && isActive && (
              <div className="flex gap-2">
                <button
                  onClick={onRun}
                  disabled={runningStep}
                  className="px-4 py-2 rounded-lg text-xs font-medium text-white flex items-center gap-1.5 disabled:opacity-30"
                  style={{
                    background: "linear-gradient(135deg, #86BC25, #6B9B1E)",
                  }}
                >
                  {runningStep ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Starting...
                    </>
                  ) : (
                    <>
                      <Play size={14} /> Run this step
                    </>
                  )}
                </button>
                {step.is_conditional && (
                  <button
                    onClick={onSkip}
                    className="px-4 py-2 rounded-lg text-xs text-white/30 hover:text-white/50 border border-white/5 hover:border-white/10 transition flex items-center gap-1.5"
                  >
                    <SkipForward size={14} /> Skip
                  </button>
                )}
              </div>
            )}

            {/* Running — go to agent + mark complete */}
            {step.status === "running" && (
              <div className="flex gap-2">
                <button
                  onClick={onLaunchAgent}
                  className="px-4 py-2 rounded-lg text-xs font-medium text-brand-400 border border-brand-500/30 hover:border-brand-500/50 flex items-center gap-1.5 transition"
                  style={{ background: "rgba(134,188,37,0.05)" }}
                >
                  <ChevronRight size={14} /> Open{" "}
                  {agentDef?.shortName || "Agent"}
                </button>
                <button
                  onClick={onComplete}
                  className="px-4 py-2 rounded-lg text-xs font-medium text-blue-400 border border-blue-500/30 hover:border-blue-500/50 flex items-center gap-1.5 transition"
                  style={{ background: "rgba(59,130,246,0.05)" }}
                >
                  <Eye size={14} /> Submit for Review
                </button>
              </div>
            )}

            {/* Waiting approval — approve / reject */}
            {step.status === "waiting_approval" && (
              <div className="space-y-3">
                <div
                  className="p-3 rounded-xl text-xs text-blue-300/80 leading-relaxed"
                  style={{
                    background: "rgba(59,130,246,0.06)",
                    border: "1px solid rgba(59,130,246,0.15)",
                  }}
                >
                  <Eye size={12} className="inline mr-1.5 text-blue-400" />
                  Review the agent's output above. Approve to pass results to
                  the next stage, or reject to re-run.
                </div>

                {/* Output preview */}
                {step.output_summary && step.output_summary !== "{}" && (
                  <OutputPreview output={step.output_summary} />
                )}

                <div className="flex gap-2">
                  <button
                    onClick={onApprove}
                    disabled={approvingStep}
                    className="px-4 py-2 rounded-lg text-xs font-medium text-green-400 border border-green-500/30 hover:border-green-500/50 flex items-center gap-1.5 transition disabled:opacity-30"
                    style={{ background: "rgba(34,197,94,0.05)" }}
                  >
                    {approvingStep ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <ThumbsUp size={14} />
                    )}
                    Approve & Continue
                  </button>
                  <button
                    onClick={onToggleRejectInput}
                    className="px-4 py-2 rounded-lg text-xs font-medium text-red-400 border border-red-500/30 hover:border-red-500/50 flex items-center gap-1.5 transition"
                    style={{ background: "rgba(239,68,68,0.05)" }}
                  >
                    <ThumbsDown size={14} /> Reject
                  </button>
                  <button
                    onClick={onLaunchAgent}
                    className="px-4 py-2 rounded-lg text-xs font-medium text-white/30 border border-white/5 hover:border-white/10 flex items-center gap-1.5 transition"
                  >
                    <ChevronRight size={14} /> Review in Agent
                  </button>
                </div>

                {/* Reject feedback input */}
                {showRejectInput && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={rejectFeedback}
                      onChange={(e) => onRejectFeedbackChange(e.target.value)}
                      placeholder="What needs to change?"
                      className="flex-1 px-3 py-2 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none border"
                      style={{
                        background: "rgba(14,14,24,0.6)",
                        borderColor: "rgba(239,68,68,0.15)",
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") onReject();
                      }}
                    />
                    <button
                      onClick={onReject}
                      disabled={approvingStep}
                      className="px-4 py-2 rounded-lg text-xs font-medium text-red-400 border border-red-500/30"
                      style={{ background: "rgba(239,68,68,0.05)" }}
                    >
                      Confirm Reject
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Approved gate info (conditional step whose HITL gate the
                reviewer approved → status `approved`). */}
            {step.status === "approved" && (
              <div className="flex items-center gap-2 text-[11px] text-green-400/60">
                <CheckCircle2 size={12} /> Approved at review gate — plan
                resumed
              </div>
            )}

            {/* Rejected gate info (status `rejected` → the plan halts here;
                the rejection reason is recorded on the HITL gate). */}
            {step.status === "rejected" && (
              <div
                className="p-2 rounded-lg text-xs text-red-300/60 border border-red-500/10"
                style={{ background: "rgba(239,68,68,0.03)" }}
              >
                <ThumbsDown size={10} className="inline mr-1" />
                Rejected at review gate — the run is halted here.
              </div>
            )}

            {/* Skipped info */}
            {step.status === "skipped" && (
              <div className="flex items-center gap-2 text-[11px] text-white/20">
                <SkipForward size={12} /> Skipped by user
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Output Preview (JSON viewer)
// ---------------------------------------------------------------------------
function OutputPreview({ output }: { output: string }) {
  const [expanded, setExpanded] = useState(false);

  let parsed: any = null;
  try {
    parsed = JSON.parse(output);
  } catch {
    // plain text
  }

  const displayText = parsed ? JSON.stringify(parsed, null, 2) : output;

  const truncated = displayText.length > 300 && !expanded;

  return (
    <div
      className="rounded-lg border border-white/5 overflow-hidden"
      style={{ background: "rgba(14,14,24,0.4)" }}
    >
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/5">
        <span className="text-[11px] text-white/20 font-medium uppercase">
          Output Preview
        </span>
        {displayText.length > 300 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[11px] text-brand-400 hover:text-brand-300"
          >
            {expanded ? "Collapse" : "Expand"}
          </button>
        )}
      </div>
      <pre className="px-3 py-2 text-[11px] text-white/50 font-mono overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap">
        {truncated ? displayText.slice(0, 300) + "..." : displayText}
      </pre>
    </div>
  );
}
